import { EyeIcon } from '@heroicons/react/24/outline'
import { trackNoticeRun } from 'components/BackgroundRuns'
import { TagMatch } from 'components/TagMatchToggle'
import { TagScopePicker } from 'components/TagScopePicker'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  type LateNoticeExcludedLevel,
  type LateNoticePreviewRow,
  cancelLateNoticeRun,
  downloadLateNotices,
  getLateNoticeConfig,
  getLateNoticeStatus,
  previewLateNotices,
  saveLateNoticeTemplates,
  startLateNoticeRun,
} from 'services'
import { Button, Tooltip } from 'stories/components'

import NoticeTemplatePreview from './NoticeTemplatePreview'

interface Level {
  key: string
  label: string
  description: string
  minDays: number
  defaultTemplate: string
}

/** The review list as a spreadsheet, so it can go out to whoever decides who is excused. */
const downloadPreviewCsv = (rows: LateNoticePreviewRow[], asOf: string) => {
  const headers = [
    'Loan #',
    // The prior servicer's number, so the list can be read beside that servicer's own report.
    'Prior Loan #',
    'Borrower',
    'Days Late',
    'Next Payment',
    'Payments Past Due',
    'Regular Payment',
    'Total Amount Due',
    'Notice',
    'Send',
  ]
  // Excel reads a leading =, + or - as a formula, so a value starting with one is prefixed.
  const cell = (v: any) => {
    const raw = String(v ?? '')
    const safe = /^[=+\-@]/.test(raw) ? `'${raw}` : raw
    return `"${safe.replace(/"/g, '""')}"`
  }
  const body = rows.map((r) =>
    [
      r.loanNumber,
      r.priorLoanNumber,
      r.borrower,
      r.daysLate,
      r.nextPayment,
      r.paymentsPastDue,
      r.regularPayment,
      r.totalAmountDue,
      r.level,
      // Left for the reviewer to overwrite with No on the loans that must not be noticed.
      'Yes',
    ].map(cell).join(','),
  )
  // The BOM keeps Excel on UTF-8, so an accented borrower name does not arrive mangled.
  const blob = new Blob(['\uFEFF' + [headers.map(cell).join(','), ...body].join('\r\n')], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `Late Notice Review - ${asOf}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * The review list coming back from whoever decides who is excused, read as loan numbers.
 *
 * Takes a bare list of loan numbers and whole rows pasted out of the exported spreadsheet (tab or
 * comma separated, quoted or not). A line whose every cell is a number is read as a list of loan
 * numbers, so `309802, 311004` on one line is two loans; any other line is a spreadsheet row and is
 * read as its FIRST integer cell, the Loan # column of the export, so a borrower name, a days-late
 * count or an amount is never mistaken for a loan.
 *
 * If any row carries an explicit Yes/No cell - the export's `Send` column, overwritten by the
 * reviewer - the paste is the whole list come home rather than a list of exceptions: only the rows
 * marked No are held back, and the rows marked Yes are put back. Reading every number in that file
 * as an exclusion would hold back the entire run.
 */
export const parseHoldBackList = (text: string) => {
  const hold: number[] = []
  const send: number[] = []
  const bare: number[] = []
  let marked = false
  for (const line of String(text || '').split(/\r?\n/)) {
    const cells = line.split(/[\t,;|]/).map((c) => c.trim().replace(/^"|"$/g, '').trim())
    const filled = cells.filter((c) => c !== '')
    const numbers = filled.filter((c) => /^\d{1,9}$/.test(c))
    if (!numbers.length) continue
    const flag = cells.filter((c) => /^(yes|no|y|n)$/i.test(c)).pop()
    if (flag) {
      marked = true
      // A marked line is a row of the export, so only its Loan # column counts.
      if (/^(no|n)$/i.test(flag)) hold.push(Number(numbers[0]))
      else send.push(Number(numbers[0]))
    } else if (numbers.length === filled.length) {
      for (const n of numbers) bare.push(Number(n))
    } else {
      bare.push(Number(numbers[0]))
    }
  }
  return { marked, hold, send, bare }
}

// Generate escalating late notices (courtesy → foreclosure) for delinquent borrowers using
// docgen templates. Auto-matches each borrower to the strongest notice their days-late meets.
export default function BorrowerLateNoticesModal({ onClose, allTags }: { onClose: () => void; allTags: string[] }) {
  const today = new Date().toISOString().slice(0, 10)
  const [loadingConfig, setLoadingConfig] = useState(true)
  const [levels, setLevels] = useState<Level[]>([])
  const [templates, setTemplates] = useState<string[]>([])
  const [docgenAvailable, setDocgenAvailable] = useState(true)
  const [templateMap, setTemplateMap] = useState<Record<string, string>>({})
  const [previewLevel, setPreviewLevel] = useState<Level | null>(null)
  const [selected, setSelected] = useState<Record<string, boolean>>({})

  const [asOf, setAsOf] = useState(today)
  const [tags, setTags] = useState<string[]>([])
  const [tagMatch, setTagMatch] = useState<TagMatch>('any')
  const [excludeTags, setExcludeTags] = useState<string[]>([])
  const [attach, setAttach] = useState(true)
  const [email, setEmail] = useState(false)
  // Blank = the ladder's own floor (30 days). A servicer that notices once its grace period is up
  // sets its own, which is why Longhorn's monthly run at 7 days saw nobody before this existed.
  const [minDaysLate, setMinDaysLate] = useState<string>('')

  const [phase, setPhase] = useState<'form' | 'review' | 'processing' | 'done'>('form')
  const [previewRows, setPreviewRows] = useState<LateNoticePreviewRow[]>([])
  // Levels the run would skip, with the borrower count on each. See the note above the review grid.
  const [excludedLevels, setExcludedLevels] = useState<LateNoticeExcludedLevel[]>([])
  const [previewing, setPreviewing] = useState(false)
  // Loan numbers explicitly unticked on the review. Absence means selected, so a loan that appears
  // on a re-preview starts ticked rather than silently inheriting someone else's exclusion.
  const [excluded, setExcluded] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')
  // The returned list, pasted back in rather than unticked by hand: on a monthly run the holdback
  // list is 150-200 loans long, which is the step this screen was still leaving to the mouse.
  const [holdBackOpen, setHoldBackOpen] = useState(false)
  const [holdBackText, setHoldBackText] = useState('')
  const [holdBackMode, setHoldBackMode] = useState<'hold' | 'only'>('hold')
  const [progress, setProgress] = useState<{ handled: number; total: number }>({ handled: 0, total: 0 })
  const [result, setResult] = useState<any>(null)
  const [jobId, setJobId] = useState<string>('')
  const [downloading, setDownloading] = useState(false)
  // A ref, not state: the poll closure is created once and must see the live value.
  const handedOff = useRef(false)
  // -1 once the run is working; >= 0 is how many portfolio runs are ahead of it.
  const [queuedBehind, setQueuedBehind] = useState(-1)

  useEffect(() => {
    getLateNoticeConfig()
      .then((res) => {
        const lv = res?.levels || []
        setLevels(lv)
        setTemplates(res?.templates || [])
        setDocgenAvailable(res?.docgenAvailable !== false)
        // A level the tenant has mapped opens on that mapping, including one they deliberately
        // cleared. `defaultTemplate` is only the name match, a guess that can never find a template
        // whose name does not carry the level - which is most of them, once a tenant names their own.
        const saved = res?.templateMap || {}
        setTemplateMap(Object.fromEntries(lv.map((l) => [l.key, saved[l.key] ?? (l.defaultTemplate || '')])))
        setSelected(Object.fromEntries(lv.map((l) => [l.key, true]))) // all levels selected by default
      })
      .catch(() => toast('Could not load the notice configuration.', { type: 'error' }))
      .finally(() => setLoadingConfig(false))
  }, [])

  /**
   * Which document a level sends is tenant configuration, not a per-run choice, so a pick is
   * saved as it is made rather than held until Generate: the modal reopens on it, and the
   * schedule runs on the same mapping. A failure has to be said out loud, because the cost of a
   * silent one is the next run going out on a template nobody chose.
   */
  const onPickTemplate = (key: string, name: string) => {
    const next = { ...templateMap, [key]: name }
    setTemplateMap(next)
    saveLateNoticeTemplates(next).catch(() => toast('Could not save the template selection.', { type: 'error' }))
  }


  // Levels that are both selected AND have a template mapped — these are what actually generate.
  const activeKeys = levels.filter((l) => selected[l.key] && templateMap[l.key]).map((l) => l.key)
  const allSelected = levels.length > 0 && levels.every((l) => selected[l.key])
  const toggleAll = () => setSelected(Object.fromEntries(levels.map((l) => [l.key, !allSelected])))

  // --- Review selection ---
  // A row with no template mapped can never produce a notice, so it is shown but not selectable:
  // ticking it would promise something the run then reports as skipped.
  const sendableRows = previewRows.filter((r) => !!r.template)
  const selectedLoanNumbers = sendableRows.filter((r) => !excluded.has(r.loanNumber)).map((r) => r.loanNumber)
  const term = search.trim().toLowerCase()
  const visibleRows = term
    ? previewRows.filter(
        (r) =>
          String(r.loanNumber).includes(term) ||
          (r.priorLoanNumber || '').toLowerCase().includes(term) ||
          (r.borrower || '').toLowerCase().includes(term),
      )
    : previewRows
  // Select-all acts on what the search has narrowed to, so filtering to a block of loans and
  // clearing them is one action rather than a hunt down the full list.
  const visibleSendable = visibleRows.filter((r) => !!r.template)
  const allVisibleSelected = visibleSendable.length > 0 && visibleSendable.every((r) => !excluded.has(r.loanNumber))
  const toggleAllVisible = () =>
    setExcluded((prev) => {
      const next = new Set(prev)
      visibleSendable.forEach((r) => (allVisibleSelected ? next.add(r.loanNumber) : next.delete(r.loanNumber)))
      return next
    })
  const toggleRow = (loanNumber: number) =>
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(loanNumber)) next.delete(loanNumber)
      else next.add(loanNumber)
      return next
    })

  /**
   * Apply a pasted list to the ticks in one action.
   *
   * A number the list names that is not on this review is reported rather than ignored: it means
   * the list was built against a different run (or a different day), and silently dropping it would
   * send a notice to a borrower someone had explicitly held back.
   */
  const applyHoldBackList = () => {
    const { marked, hold, send, bare } = parseHoldBackList(holdBackText)
    const known = new Set(sendableRows.map((r) => r.loanNumber))
    const listed = marked ? [...hold, ...send] : bare
    if (!listed.length) {
      toast('No loan numbers found in that list.', { type: 'error' })
      return
    }
    const unknown = listed.filter((n) => !known.has(n))
    setExcluded((prev) => {
      const next = new Set(prev)
      if (marked) {
        for (const n of hold) if (known.has(n)) next.add(n)
        for (const n of send) next.delete(n)
      } else if (holdBackMode === 'only') {
        for (const n of known) next.add(n)
        for (const n of bare) if (known.has(n)) next.delete(n)
      } else {
        for (const n of bare) if (known.has(n)) next.add(n)
      }
      return next
    })
    const held = marked
      ? hold.filter((n) => known.has(n)).length
      : holdBackMode === 'only'
      ? known.size - bare.filter((n) => known.has(n)).length
      : bare.filter((n) => known.has(n)).length
    const tail = unknown.length
      ? ` · ${unknown.length.toLocaleString()} not on this review (${unknown.slice(0, 5).join(', ')}${
          unknown.length > 5 ? '…' : ''
        })`
      : ''
    toast(`${held.toLocaleString()} held back${tail}`, { type: unknown.length ? 'warning' : 'success' })
    setHoldBackOpen(false)
    setHoldBackText('')
  }

  const minDays = minDaysLate.trim() === '' ? undefined : Number(minDaysLate)

  /** Pull the list this run would cover, so it can be reviewed (and exported) before it runs. */
  const onPreview = async () => {
    if (!activeKeys.length) {
      toast('Select at least one notice level (with a template mapped).', { type: 'error' })
      return
    }
    setPreviewing(true)
    const res = await previewLateNotices({
      asOf,
      tags,
      tagMatch,
      excludeTags,
      templateMap,
      levels: activeKeys,
      minDaysLate: minDays,
    }).catch(() => null)
    setPreviewing(false)
    if (!res) {
      toast('Could not load the delinquent borrowers.', { type: 'error' })
      return
    }
    setPreviewRows(res.rows || [])
    setExcludedLevels(res.excludedByLevel || [])
    setExcluded(new Set())
    setSearch('')
    setHoldBackOpen(false)
    setHoldBackText('')
    setPhase('review')
  }

  const onGenerate = async () => {
    if (!activeKeys.length) {
      toast('Select at least one notice level (with a template mapped).', { type: 'error' })
      return
    }
    if (!selectedLoanNumbers.length) {
      toast('Select at least one borrower.', { type: 'error' })
      return
    }
    setPhase('processing')
    setProgress({ handled: 0, total: 0 })
    const start: any = await startLateNoticeRun({
      asOf,
      tags,
      tagMatch,
      excludeTags,
      templateMap,
      levels: activeKeys,
      minDaysLate: minDays,
      loanNumbers: selectedLoanNumbers,
      attach,
      email,
    }).catch(() => null)
    const id = start?.jobId
    if (!id) {
      setPhase('review')
      toast('Could not start the run.', { type: 'error' })
      return
    }
    setJobId(id)
    const poll = async () => {
      if (handedOff.current) return
      const st: any = await getLateNoticeStatus(id).catch(() => null)
      if (!st || st.success === 0) {
        setPhase('review')
        toast(st?.message || 'Lost track of the run.', { type: 'error' })
        return
      }
      setQueuedBehind(st.queued ? st.queuedBehind ?? 0 : -1)
      setProgress({ handled: st.handled || 0, total: st.total || 0 })
      if (st.done) {
        if (st.error) {
          setPhase('review')
          toast(st.error, { type: 'error' })
        } else {
          setResult(st.result)
          setPhase('done')
        }
        return
      }
      setTimeout(poll, 1000)
    }
    poll()
  }

  /** Stop the run. Notices already generated are kept, so this closes rather than pretending. */
  const onCancelRun = async () => {
    if (!jobId) return
    handedOff.current = true
    setPhase('review')
    await cancelLateNoticeRun(jobId).catch(() => null)
    toast('Stopping the run. Notices already generated are kept.', { type: 'warning' })
    onClose()
  }

  /** Hand the run to the background tray and close. The run carries on regardless. */
  const onRunInBackground = () => {
    if (!jobId) return
    handedOff.current = true
    trackNoticeRun(jobId, 'Borrower Late Notices', 'late')
    toast('Running in the background. You will be notified when it is ready.', { type: 'info' })
    onClose()
  }

  const onDownload = async () => {
    setDownloading(true)
    try {
      await downloadLateNotices(jobId)
    } catch {
      toast('No combined document was produced for this run.', { type: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const pct = progress.total ? Math.min(100, Math.round((progress.handled / progress.total) * 100)) : 0
  const generated = result?.generated || []
  const skipped = result?.skipped || []

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-[640px] max-w-[95vw] flex-col rounded-lg bg-white p-5 shadow-xl">
        <h2 className="mb-1 text-lg font-bold">Borrower Late Notices</h2>

        {phase === 'form' && (
          <>
            <p className="mb-3 text-sm text-gray-500">
              Scans servicing loans, matches each delinquent borrower to the strongest notice their days-late qualifies
              for, and generates it from your docgen templates.
            </p>
            {loadingConfig ? (
              <p className="py-8 text-center text-sm text-gray-400">Loading templates…</p>
            ) : (
              <div className="flex-1 overflow-auto">
                {!docgenAvailable && (
                  <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    The document generation service could not be reached, so no templates were loaded. You can still set
                    the options, but generation will fail until docgen is available.
                  </div>
                )}
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Notices to Generate
                  </span>
                  <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-gray-500">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    Select all
                  </label>
                </div>
                <p className="mb-2 text-[11px] text-gray-400">
                  A borrower with a Preferred Language recorded gets the matching template when one exists: name it
                  after the template chosen here, as <span className="font-medium">Template Name (Spanish)</span>. Where
                  there is no such template the notice goes out on the one selected here.
                </p>
                <div className="overflow-hidden rounded border border-slate-200">
                  <table className="min-w-full text-sm">
                    <tbody>
                      {levels.map((l) => (
                        <tr key={l.key} className="border-t border-slate-100 first:border-t-0">
                          <td className="w-8 px-3 py-2 align-top">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={!!selected[l.key]}
                              onChange={(e) => setSelected((s) => ({ ...s, [l.key]: e.target.checked }))}
                            />
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className={`font-medium ${selected[l.key] ? 'text-gray-800' : 'text-gray-400'}`}>
                              {l.label}
                            </div>
                            <div className="text-[11px] text-gray-400">{l.description}</div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <select
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                                value={templateMap[l.key] || ''}
                                disabled={!selected[l.key]}
                                onChange={(e) => onPickTemplate(l.key, e.target.value)}
                              >
                                <option value="">— No template —</option>
                                {templates.map((t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ))}
                              </select>
                              <Tooltip message="Preview this notice">
                                <button
                                  type="button"
                                  className="rounded p-1 text-gray-500 hover:bg-slate-100 hover:text-shade-blue disabled:opacity-30"
                                  disabled={!templateMap[l.key]}
                                  onClick={() => setPreviewLevel(l)}
                                >
                                  <EyeIcon className="h-5 w-5" />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="text-sm text-gray-600">
                    As-of Date
                    <input
                      type="date"
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      value={asOf}
                      onChange={(e) => setAsOf(e.target.value)}
                    />
                  </label>
                  <label className="text-sm text-gray-600">
                    Send From (days past due)
                    <input
                      type="number"
                      min={1}
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                      placeholder={`Default ${levels[0]?.minDays ?? 30}`}
                      value={minDaysLate}
                      onChange={(e) => setMinDaysLate(e.target.value)}
                    />
                    <span className="mt-1 block text-[11px] text-gray-400">
                      Blank uses {levels[0]?.label || 'the first notice'} at {levels[0]?.minDays ?? 30} days. Set it
                      lower to notice as soon as your grace period is up.
                    </span>
                  </label>
                  <div className="text-sm text-gray-600">
                    Delivery
                    <div className="mt-1 flex flex-col gap-1">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked disabled />
                        Download combined PDF
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} />
                        Attach to each loan
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
                        Email borrowers
                      </label>
                    </div>
                  </div>
                </div>

                <TagScopePicker
                  className="mt-3"
                  allTags={allTags}
                  tags={tags}
                  onChangeTags={setTags}
                  tagMatch={tagMatch}
                  onChangeTagMatch={setTagMatch}
                  excludeTags={excludeTags}
                  onChangeExcludeTags={setExcludeTags}
                />
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button color="gray" outline onClick={onClose}>
                Cancel
              </Button>
              <Button color="blue" onClick={onPreview} loading={previewing} disabled={loadingConfig || !activeKeys.length}>
                Review Borrowers
              </Button>
            </div>
          </>
        )}

        {phase === 'review' && (
          <>
            <p className="mb-2 text-sm text-gray-500">
              Every borrower a notice would go to. Untick the ones you are holding back, then
              generate. Export the list first if someone else decides who is excused.
            </p>
            {/*
              A level that is off takes its whole cohort off this list. Said out loud, because a
              short list otherwise reads as the whole delinquent book: Longhorn's cross-check against
              their prior servicer came up 89 borrowers short this way, all of them 150+ days down.
            */}
            {excludedLevels.length > 0 && (
              <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                <span className="font-medium">Not on this review:</span>{' '}
                {excludedLevels
                  .map(
                    (l) =>
                      `${l.count.toLocaleString()} borrower${l.count === 1 ? '' : 's'} at ${l.description.toLowerCase()} (${l.label}${
                        templateMap[l.key] ? ' is turned off' : ' has no template mapped'
                      })`,
                  )
                  .join(', ')}
                . Go back and include that level to notice them too.
              </div>
            )}
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <input
                className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
                placeholder="Filter by loan number, prior loan number or borrower…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[12px] text-gray-500">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  disabled={!visibleSendable.length}
                  onChange={toggleAllVisible}
                />
                Select all{term ? ' shown' : ''}
              </label>
              <Button color="gray" outline onClick={() => downloadPreviewCsv(previewRows, asOf)}>
                Export List
              </Button>
              <Button color="gray" outline onClick={() => setHoldBackOpen((v) => !v)}>
                Apply Returned List
              </Button>
            </div>
            {holdBackOpen && (
              <div className="mb-2 rounded border border-slate-200 bg-slate-50 p-3">
                <p className="mb-2 text-[12px] text-gray-600">
                  Paste the list that came back. Loan numbers alone, or whole rows out of the exported
                  spreadsheet — if its <span className="font-semibold">Send</span> column has been marked
                  Yes/No, only the rows marked No are held back.
                </p>
                <textarea
                  className="h-24 w-full rounded border border-gray-300 px-2 py-1.5 font-mono text-[12px]"
                  placeholder={'309802\n310735\n311004'}
                  value={holdBackText}
                  onChange={(e) => setHoldBackText(e.target.value)}
                />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-3 text-[12px] text-gray-600">
                    <label className="flex cursor-pointer items-center gap-1.5">
                      <input
                        type="radio"
                        checked={holdBackMode === 'hold'}
                        onChange={() => setHoldBackMode('hold')}
                      />
                      Hold these back
                    </label>
                    <label className="flex cursor-pointer items-center gap-1.5">
                      <input
                        type="radio"
                        checked={holdBackMode === 'only'}
                        onChange={() => setHoldBackMode('only')}
                      />
                      Send only these
                    </label>
                    <span className="text-gray-400">(ignored when the list is marked Yes/No)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button color="gray" outline onClick={() => setHoldBackOpen(false)}>
                      Cancel
                    </Button>
                    <Button color="blue" onClick={applyHoldBackList} disabled={!holdBackText.trim()}>
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <p className="mb-2 text-[12px] text-gray-500">
              <span className="font-semibold text-gray-800">{selectedLoanNumbers.length.toLocaleString()}</span> of{' '}
              {sendableRows.length.toLocaleString()} selected
              {previewRows.length !== sendableRows.length && (
                <span className="text-amber-700">
                  {' · '}
                  {(previewRows.length - sendableRows.length).toLocaleString()} with no template mapped
                </span>
              )}
              {term ? ` · showing ${visibleRows.length.toLocaleString()}` : ''}
            </p>
            <div className="flex-1 overflow-auto">
              {previewRows.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">
                  No borrower is {minDays ?? levels[0]?.minDays ?? 30} or more days past due in this scope.
                </p>
              ) : (
                <div className="overflow-hidden rounded border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="w-8 px-3 py-1.5" />
                        <th className="px-3 py-1.5 text-left">Loan #</th>
                        <th className="px-3 py-1.5 text-left">Prior Loan #</th>
                        <th className="px-3 py-1.5 text-left">Borrower</th>
                        <th className="px-3 py-1.5 text-left">Notice</th>
                        <th className="px-3 py-1.5 text-left">Next Payment</th>
                        <th className="px-3 py-1.5 text-right">Days Late</th>
                        <th className="px-3 py-1.5 text-right">Total Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((r) => {
                        const sendable = !!r.template
                        const on = sendable && !excluded.has(r.loanNumber)
                        return (
                          <tr
                            key={r.loanNumber}
                            className={`border-t border-slate-100 ${on ? '' : 'bg-slate-50 text-gray-400'}`}
                          >
                            <td className="px-3 py-1.5">
                              <input
                                type="checkbox"
                                checked={on}
                                disabled={!sendable}
                                onChange={() => toggleRow(r.loanNumber)}
                              />
                            </td>
                            <td className="px-3 py-1.5">{r.loanNumber}</td>
                            <td className="px-3 py-1.5">{r.priorLoanNumber || ''}</td>
                            <td className="px-3 py-1.5">{r.borrower}</td>
                            <td className="px-3 py-1.5">
                              {r.level}
                              {!sendable && <span className="block text-[11px] text-amber-700">No template mapped</span>}
                            </td>
                            <td className="px-3 py-1.5">{r.nextPayment}</td>
                            <td className="px-3 py-1.5 text-right">{r.daysLate}</td>
                            <td className="px-3 py-1.5 text-right">{r.totalAmountDue}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button color="gray" outline onClick={() => setPhase('form')}>
                Back
              </Button>
              <Button color="blue" onClick={onGenerate} disabled={!selectedLoanNumbers.length}>
                Generate {selectedLoanNumbers.length.toLocaleString()} Notice
                {selectedLoanNumbers.length === 1 ? '' : 's'}
              </Button>
            </div>
          </>
        )}

        {phase === 'processing' && (
          <>
            <p className="mb-4 text-sm text-gray-500">
              {queuedBehind >= 0
                ? queuedBehind > 0
                  ? `Waiting for ${queuedBehind} earlier run to finish. Portfolio runs go one at a time so they cannot slow each other, or the rest of the app, down. `
                  : 'Waiting to start. '
                : ''}
              Generating notices for delinquent borrowers. Leave this open, or send it to the background and carry on working.
            </p>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-shade-blue transition-all duration-300"
                style={{ width: progress.total ? `${pct}%` : '8%' }}
              />
            </div>
            <p className="mt-2 text-center text-sm text-gray-600">
              {progress.total
                ? `${progress.handled.toLocaleString()} of ${progress.total.toLocaleString()} (${pct}%)`
                : 'Scanning for delinquent borrowers…'}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button color="gray" outline onClick={onCancelRun} disabled={!jobId}>
                Cancel Run
              </Button>
              <Button color="blue" onClick={onRunInBackground} disabled={!jobId}>
                Run in Background
              </Button>
            </div>
            <p className="mt-2 text-center text-[11px] text-gray-400">
              Cancelling stops between borrowers. Notices already generated are kept.
            </p>
          </>
        )}

        {phase === 'done' && (
          <>
            <p className="mb-3 text-sm text-gray-600">
              <span className="font-semibold text-green-700">{generated.length}</span> generated
              {' · '}
              <span className="font-semibold text-amber-700">{skipped.length}</span> skipped
            </p>
            <div className="flex-1 overflow-auto">
              {generated.length > 0 && (
                <div className="mb-4">
                  <div className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Generated ({generated.length})
                  </div>
                  <div className="overflow-hidden rounded border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Loan #</th>
                          <th className="px-3 py-1.5 text-left">Borrower</th>
                          <th className="px-3 py-1.5 text-left">Notice</th>
                          <th className="px-3 py-1.5 text-left">Language</th>
                          <th className="px-3 py-1.5 text-right">Days Late</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generated.map((g: any) => (
                          <tr key={g.loanNumber} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{g.loanNumber}</td>
                            <td className="px-3 py-1.5">{g.borrower}</td>
                            <td className="px-3 py-1.5">{g.level}</td>
                            {/* Blank unless the ladder found a template in the borrower's language. */}
                            <td className="px-3 py-1.5">{g.language || ''}</td>
                            <td className="px-3 py-1.5 text-right">{g.daysLate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {skipped.length > 0 && (
                <div>
                  <div className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    Skipped ({skipped.length})
                  </div>
                  <div className="overflow-hidden rounded border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Loan #</th>
                          <th className="px-3 py-1.5 text-left">Borrower</th>
                          <th className="px-3 py-1.5 text-left">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skipped.map((s: any, i: number) => (
                          <tr key={`${s.loanNumber}-${i}`} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{s.loanNumber}</td>
                            <td className="px-3 py-1.5">{s.borrower}</td>
                            <td className="px-3 py-1.5 text-amber-700">{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!generated.length && !skipped.length && (
                <p className="text-sm text-gray-400">No delinquent borrowers were found in scope.</p>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              {generated.length > 0 && (
                <Button color="gray" outline onClick={onDownload} loading={downloading}>
                  Download Combined PDF
                </Button>
              )}
              <Button color="blue" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>

      {previewLevel && (
        <NoticeTemplatePreview
          kind="late"
          level={previewLevel.key}
          levelLabel={previewLevel.label}
          template={templateMap[previewLevel.key]}
          onClose={() => setPreviewLevel(null)}
        />
      )}
    </div>
  )
}
